/**
 * Diálogo de configuração do node Teste A/B
 * Permite configurar variantes (A, B, C, D) com porcentagens que somam 100%
 */

import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Shuffle, Plus, Trash2 } from 'lucide-react';
import { FlowNode, ABTestVariant } from '@/types/flows';
import { toast } from 'sonner';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';

interface ConfigureABTestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode;
  onSave: (nodeId: string, config: any) => void;
}

// Labels fixas das variantes
const VARIANT_LABELS = ['A', 'B', 'C', 'D'];

// Configuração padrão ao criar node
const DEFAULT_VARIANTS: ABTestVariant[] = [
  { id: 'variant_0', label: 'A', percentage: 50 },
  { id: 'variant_1', label: 'B', percentage: 50 },
];

/**
 * Componente de configuração do Teste A/B
 */
export function ConfigureABTestDialog({
  open,
  onOpenChange,
  node,
  onSave,
}: ConfigureABTestDialogProps) {
  const [variants, setVariants] = useState<ABTestVariant[]>(DEFAULT_VARIANTS);
  const nicknameRef = useRef<NodeIdFieldRef>(null);

  // Carrega configuração existente
  useEffect(() => {
    if (open && node.data.config?.variants) {
      setVariants(node.data.config.variants);
    } else if (open) {
      // Usa default ao criar novo node
      setVariants(DEFAULT_VARIANTS);
    }
  }, [open, node.data.config]);

  /**
   * Atualiza porcentagem de uma variante com auto-cálculo
   */
  const handlePercentageChange = (index: number, newValue: number) => {
    const clampedValue = Math.min(100, Math.max(0, newValue));
    const newVariants = [...variants];
    newVariants[index].percentage = clampedValue;
    
    // Calcula total das outras variantes
    const othersTotal = newVariants.reduce((sum, v, i) => 
      i !== index ? sum + v.percentage : sum, 0);
    
    // Se ultrapassar 100%, ajusta proporcionalmente as outras
    if (clampedValue + othersTotal > 100) {
      const available = 100 - clampedValue;
      const ratio = othersTotal > 0 ? available / othersTotal : 0;
      
      newVariants.forEach((v, i) => {
        if (i !== index) {
          v.percentage = Math.round(v.percentage * ratio);
        }
      });
    }
    
    // Garante soma exata = 100
    const total = newVariants.reduce((sum, v) => sum + v.percentage, 0);
    if (total !== 100 && newVariants.length > 1) {
      // Encontra a última variante diferente da editada
      let lastOtherIndex = -1;
      for (let i = newVariants.length - 1; i >= 0; i--) {
        if (i !== index) {
          lastOtherIndex = i;
          break;
        }
      }
      if (lastOtherIndex !== -1) {
        newVariants[lastOtherIndex].percentage += (100 - total);
      }
    }
    
    setVariants(newVariants);
  };

  /**
   * Adiciona nova variante (C ou D)
   */
  const addVariant = () => {
    if (variants.length >= 4) {
      toast.error('Máximo de 4 variantes (A, B, C, D)');
      return;
    }
    
    const newLabel = VARIANT_LABELS[variants.length];
    
    // Redistribui porcentagens igualmente
    const newPercentage = Math.floor(100 / (variants.length + 1));
    const newVariants = variants.map(v => ({
      ...v,
      percentage: newPercentage,
    }));
    
    // Adiciona nova variante
    newVariants.push({
      id: `variant_${variants.length}`,
      label: newLabel,
      percentage: 100 - (newPercentage * variants.length), // Resto para a última
    });
    
    setVariants(newVariants);
  };

  /**
   * Remove variante (apenas C e D podem ser removidas)
   */
  const removeVariant = (index: number) => {
    // Não permite remover A e B (índices 0 e 1)
    if (index < 2) {
      toast.error('As variantes A e B são obrigatórias');
      return;
    }
    
    if (variants.length <= 2) {
      toast.error('Mínimo de 2 variantes');
      return;
    }
    
    const removedPercentage = variants[index].percentage;
    const newVariants = variants.filter((_, i) => i !== index);
    
    // Redistribui a porcentagem removida entre as restantes
    const addEach = Math.floor(removedPercentage / newVariants.length);
    newVariants.forEach(v => v.percentage += addEach);
    
    // Ajusta resto para garantir soma = 100
    const total = newVariants.reduce((sum, v) => sum + v.percentage, 0);
    if (total !== 100) {
      newVariants[0].percentage += (100 - total);
    }
    
    setVariants(newVariants);
  };

  /**
   * Calcula total das porcentagens
   */
  const totalPercentage = variants.reduce((sum, v) => sum + v.percentage, 0);

  /**
   * Salva configuração
   */
  const handleSave = () => {
    // Valida soma = 100%
    if (Math.abs(totalPercentage - 100) > 0.01) {
      toast.error(`As porcentagens devem somar 100% (atual: ${totalPercentage}%)`);
      return;
    }
    
    // Valida mínimo de variantes
    if (variants.length < 2) {
      toast.error('Configure pelo menos 2 variantes (A e B)');
      return;
    }
    
    onSave(node.id, { variants, nickname: nicknameRef.current?.getNickname() });
    onOpenChange(false);
    toast.success('Teste A/B configurado');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="p-1.5 rounded bg-pink-500">
              <Shuffle className="h-4 w-4 text-white" />
            </div>
            Configurar Teste A/B
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* ID do Node */}
          <NodeIdField ref={nicknameRef} nodeId={node.id} defaultNickname={node?.data.config?.nickname} />

          {/* Descrição */}
          <p className="text-sm text-muted-foreground">
            Configure a distribuição do fluxo entre as variantes. 
            As porcentagens devem somar 100%.
          </p>

          {/* Lista de variantes */}
          <div className="space-y-4">
            {variants.map((variant, index) => (
              <div key={variant.id} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm bg-pink-500 text-white px-2 py-0.5 rounded">
                      {variant.label}
                    </span>
                    <Label className="text-sm">
                      Variante {variant.label}
                    </Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {/* Input de porcentagem */}
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={variant.percentage}
                      onChange={(e) => handlePercentageChange(index, parseInt(e.target.value) || 0)}
                      className="w-20 text-center"
                    />
                    <span className="text-sm text-muted-foreground">%</span>
                    
                    {/* Botão remover (apenas C e D) */}
                    {index >= 2 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeVariant(index)}
                        className="h-8 w-8 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Slider */}
                <Slider
                  value={[variant.percentage]}
                  onValueChange={([value]) => handlePercentageChange(index, value)}
                  max={100}
                  min={0}
                  step={1}
                  className="w-full"
                />
              </div>
            ))}
          </div>

          {/* Botão adicionar variante */}
          {variants.length < 4 && (
            <Button
              variant="outline"
              onClick={addVariant}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Variante {VARIANT_LABELS[variants.length]}
            </Button>
          )}

          {/* Total */}
          <div className={`flex items-center justify-between p-3 rounded-lg ${
            totalPercentage === 100 
              ? 'bg-green-500/10 border border-green-500/30' 
              : 'bg-destructive/10 border border-destructive/30'
          }`}>
            <span className="font-medium">Total</span>
            <span className={`font-bold ${
              totalPercentage === 100 ? 'text-green-600' : 'text-destructive'
            }`}>
              {totalPercentage}%
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={totalPercentage !== 100}>
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
